import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import React from 'react';
import SessionStore from 'stores/shared/session';
import _ from 'underscore';
import { colors } from '@anyroad/theme';
import ModalActionCreator from 'action_creators/modal';
import ConfirmationBody from 'views/plugins/confirmation/body';
import ConfirmationSummary from 'views/plugins/confirmation/summary';
import IntegrationActionCreator from 'action_creators/integration';
import MessageGuide from 'views/plugins/confirmation/message_guide';
import PartnerHeader from 'ui_system/headers/PartnerHeader';
import { t, loadLocales } from 'services/translation';
import PoweredBy from 'views/plugins/checkout/powered_by';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { Theme } from 'ui_system/theme';
import { getTranslatedText } from '../helpers';
import { trackCheckoutConfirmationArriveOnPage } from '../segmentTracking';

import './index.scss';

const SendMessage = styled.a`
  color: ${colors.primary};
`;

class ConfirmationDefault extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: this.props.request.email,
      displayError: false,
    };
  }

  static displayName = 'ConfirmationDefault-Plugins';

  static propTypes = {
    pricing: PropTypes.object.isRequired,
    request: PropTypes.object.isRequired,
    tour: PropTypes.object.isRequired,
  };

  // If not a regular tour
  isCustomTour = () => {
    const { request } = this.props;
    return request.is_invoice || request.for_custom_booking;
  };

  summaryProps = () => {
    const { request, pricing, tour, is_live_booking, partner_info } =
      this.props;
    return {
      is_live_booking,

      picture: {
        tour_picture: this.isCustomTour()
          ? request.guide_avatar
          : request.tour_picture,
        rounded: this.isCustomTour(),
        hideOnMobile: true,
      },

      header: {
        tour_name: request.tour_name,
        tour_name_translation: request.tour_name_translation,
        guide_full_name: request.guide_full_name,
        has_resource: request.has_resource,
        resource_name: request.resource_name,
        business_name: partner_info.business_name,
      },

      requestInfo: {
        date: request.date,
        time: request.time,
        duration: request.duration,
        number_of_guests: request.number_of_guests,
        committed: request.committed,
        root_unit: request.root_unit,
        isoTime: request.iso_time,
        online: tour.online,
        tourTimezone: tour.tour_timezone,
      },

      cancellationPolicy: {
        description: request.tour_cancellation_policy.description,
        name: request.tour_cancellation_policy.name,
        has_cancellation: request.has_cancellation,
        noShow: request.paid_in_cash || request.for_custom_booking,
        bookingId: request.booking_id,
        time: request.time,
      },

      pricing: {
        display_subtotal_icon: true,
        toggleable_breakdown: false,
        expanded_price_breakdown: true,
        date: request.date,
        time: request.time,
        paid_in_cash: pricing.paid_in_cash,
        subtotal: pricing.subtotal,
        bookingId: request.booking_id,
      },
    };
  };

  UNSAFE_componentWillMount() {
    const locale = this.props.tour
      ? this.props.tour.i18n_locale
      : this.props.i18n_locale;
    loadLocales(SessionStore.state.get('i18n') || locale); // TODO: replace props.tour.i18n etc with session store throughout front end
  }

  componentDidMount() {
    if (window !== top) {
      IntegrationActionCreator.startScrollingIntercom();
    }
    const tourId = this.props.tour?.id || this.props.request?.tour_id;
    const bookingId =
      this.props.request?.booking_id_hash || this.props.request?.booking_id;
    trackCheckoutConfirmationArriveOnPage({
      flags: this.props.flags,
      bookingId,
      tourId,
      guideId: this.props.request?.guide_id,
      guideName: this.props.request?.guide_full_name,
      checkoutFlow: 'legacy_web',
      device: Theme.mobile.isMobile ? 'mobile' : 'web',
      pageName: 'Checkout Confirmation',
    });
  }

  componentWillUnmount() {
    if (window !== top) {
      IntegrationActionCreator.stopScrollingIntercom();
    }
  }

  setEmail = (value) => {
    let displayError;
    //check if email is valid
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      displayError = false;
    } else {
      displayError = true;
    }
    //Update the value in the input box regardless of validity
    this.setState({
      displayError,
      email: value,
    });
  };

  _showMessageGuideModal = (e) => {
    e.preventDefault();
    ModalActionCreator.show({
      content: (
        <MessageGuide {...this._messageGuideProps()} email={this.state.email} />
      ),
      triggeringElement: e.currentTarget,
      className: 'Confirmation-Message-Guide-Modal small',
    });
  };

  _messageGuideProps = () => {
    return {
      tourist_email: this.props.request.email,
      guide_full_name: this.props.request.guide_full_name,
      sender_id: this.props.request.tourist_id_hash,
      recipient_id: this.props.request.guide_id_hash,
      booking_id: this.props.request.booking_id,
      message: '',
    };
  };

  _messageGuideFooterContent = () => {
    const {
      is_live_booking: isLiveBooking,
      is_booking_affiliate: isBookingAffiliate,
    } = this.props;

    if (isLiveBooking) return null;
    if (isBookingAffiliate) return null;

    return (
      <p aria-label="4">
        {t('HAVE_QUESTIONS_FOR_US')}?<br />
        <SendMessage
          href="#"
          onClick={this._showMessageGuideModal}
          className="brand-color"
          data-testid="guest-message-booking-confirmation"
        >
          {t('SEND_A_MESSAGE')}
        </SendMessage>
      </p>
    );
  };

  bodyProps = () => {
    const { tour = {} } = this.props || {};
    const { kiosk_setting = {}, i18n_locale: locale } = tour || {};

    const {
      headline,
      headline_translations: headlineTranslations,
      sub_headline: subHeadline,
      sub_headline_translations: subHeadlineTranslations,
    } = kiosk_setting || {};

    const props = {
      guide_full_name: this.props.request.guide_full_name,
      tourist_email: this.props.request.email,
      recipient_id: this.props.request.guide_id,
      sender_id: this.props.request.tourist_id,
      booking_id: this.props.request.booking_id,
      booking_id_hash: this.props.booking_id_hash,
      automatic_booking: this.props.is_auto_booking,
      guest_reschedulable: this.props.tour.guest_reschedulable,
      link_to_modify: this.props.request.link_to_modify,
      has_future_availability: this.props.request.has_future_availability,
      redirect_url: this.props.request.redirect_url,
      footer_content: this._messageGuideFooterContent(),
      custom_title: this.props.tour.translated_confirmation_title_text,
      custom_body: this.props.tour.translated_confirmation_body_text,
      guest_paid_invoice: this.props.guest_paid_invoice,
      show_cc_info: this.props.pricing.subtotal > 0,
      ticketed_event: this.props.request.ticketed_event,
      has_multiple_tickets: this.props.request.has_multiple_tickets,
      has_resource: this.props.request.has_resource,
      resource_name: this.props.request.resource_name,
      custom_locations_enabled: this.props.tour.custom_locations_enabled,
      location: this.props.location,
      isoTime: this.props.request.iso_time,
      online: this.props.tour.online,
      tourTimezone: this.props.tour.tour_timezone,
      experience_link: this.props.request.experience_link,
      is_live_booking: this.props.is_live_booking,
      brand_color: this.props.partner_info.brand_color,
      canAdditionalGuestsLink: this.props.can_view_guest_information_page,
      AdditionalGuestsLinkSettings: this.props.additional_guest_settings,
      disable_guest_confirmation_email:
        this.props.disable_guest_confirmation_email,
      affiliate_portal_link: this.props.affiliate_portal_link,
      numberOfGuests: this.props.request.number_of_guests,
      maxNumberGuests: this.props.request.guest_of_guest_max_guests,
      isBookingPaid: this.props.is_booking_paid,
      isBookingPending: this.props.is_booking_pending,
      affiliateName: this.props.affiliate_name,
      headline: getTranslatedText(headline, headlineTranslations, locale),
      sub_headline: getTranslatedText(
        subHeadline,
        subHeadlineTranslations,
        locale
      ),
      isCapacityPerTicket: tour.is_capacity_per_ticket_type,
      isSbp: tour.is_sbp,
    };

    if (this.props.is_auto_booking || this.props.guest_paid_invoice) {
      _.extend(props, {
        first_name: this.props.request.first_name,
        date: this.props.request.date,
        time: this.props.request.time,
      });
    }

    if (this.props.pricing.paid_in_cash) {
      _.extend(props, {
        paid_in_cash: true,
      });
    }

    return props;
  };

  _renderPartnerHeader() {
    const { partner_info, is_live_booking } = this.props;
    const { business_name, logo, brand_color } = partner_info;
    return (
      <PartnerHeader
        hideOnMobile
        isLiveHeader={is_live_booking}
        bgColor={brand_color}
        title={business_name}
        logo={logo}
      />
    );
  }

  _renderLogo() {
    const styling = { textAlign: 'center', paddingBottom: '30px' };
    return (
      <span style={styling}>
        <PoweredBy hidePolicyLinks={true} />
      </span>
    );
  }

  render() {
    const {
      guest_paid_invoice,
      is_live_booking,
      is_booking_affiliate: isBookingAffiliate,
    } = this.props;
    return (
      <div>
        {(guest_paid_invoice || is_live_booking) && this._renderPartnerHeader()}
        <div
          data-testid="checkout-confirmation"
          className={`Confirmation ConfirmationDefault t-Plugins ${
            is_live_booking && 'liveConfirmation'
          }`}
        >
          <div className="ui two column centered grid Confirmation-grid">
            <div
              className={`ConfirmationDefault-Column left column ${
                is_live_booking && 'live'
              }`}
            >
              <ConfirmationBody
                {...this.bodyProps()}
                displayError={this.state.displayError}
                email={this.state.email}
                setEmail={this.setEmail}
                isBookingAffiliate={isBookingAffiliate}
              />
            </div>
            <div className="ConfirmationDefault-Column right floated column">
              <ConfirmationSummary {...this.summaryProps()} />
            </div>
          </div>
        </div>
        {is_live_booking && this._renderLogo()}
      </div>
    );
  }
}

export default withLDConsumer()(ConfirmationDefault);
